import * as React from "react"
import { css } from "@emotion/core"
import { bool, array, func } from "prop-types"
import { v4 as uuidv4, validate as uuidValidate } from "uuid"
import { Box, Button, StackView } from "@planningcenter/tapestry-react"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"
import ShowSessions from "./ShowSessions"
import EditSessions from "./EditSessions"
import { defaultTimeWithHour } from "../utils/dateFormatter"

const datePickerWrapperStyle = css`
  .DayPickerInput {
    display: block;
  }
`

const SessionsToDisplay = ({
  isEditing,
  removeAppEventSession,
  sessions,
  showInfoAlert,
  updateAppEventSession
}) => {
  if (sessions.length && isEditing) {
    return (
      <EditSessions
        removeAppEventSession={removeAppEventSession}
        sessions={sessions}
        showInfoAlert={showInfoAlert}
        updateAppEventSession={updateAppEventSession}
      />
    )
  } else if (sessions.length && !isEditing) {
    return <ShowSessions sessions={sessions} />
  } else {
    return null
  }
}

SessionsToDisplay.propTypes = {
  isEditing: bool,
  sessions: array,
  showInfoAlert: bool,
  updateAppEventSession: func,
  removeAppEventSession: func
}

const Sessions = ({ isEditing, onAppEventChange, sessions, showInfoAlert = false }) => {
  const defaultSession = {
    id: uuidv4(),
    showsAt: defaultTimeWithHour(9),
    hidesAt: defaultTimeWithHour(17)
  }

  const handleClick = () => {
    addAppEventSession(defaultSession)
  }

  const addAppEventSession = () =>
    onAppEventChange(appEvent => ({
      ...appEvent,
      appEventSessions: [...appEvent.appEventSessions, defaultSession]
    }))

  const updateAppEventSession = (id, changes) =>
    onAppEventChange(appEvent => ({
      ...appEvent,
      appEventSessions: appEvent.appEventSessions.map(s => (s.id === id ? { ...s, ...changes } : s))
    }))

  const removeAppEventSession = id =>
    onAppEventChange(appEvent => ({
      ...appEvent,
      appEventSessions: setSessionsToRemove(id, appEvent.appEventSessions)
    }))

  const setSessionsToRemove = (id, appEventSessions) =>
    appEventSessions.reduce((acc, session) => {
      if (session.id === id && uuidValidate(session.id)) return acc
      else if (session.id === id) return [...acc, { ...session, _destroy: true }]

      return [...acc, session]
    }, [])

  return (
    <>
      <StackView axis="horizontal" alignment="center" gap={1}>
        <h2 className="label">Sessions</h2>
        <HelperShelfTrigger id="check-ins-sessions-desc" />
      </StackView>
      <HelperShelfMessage id="check-ins-sessions-desc">
        These are the dates and times when your event will be available on Check-Ins stations. An
        attendance report will be created in Registrations for each session you specify.
      </HelperShelfMessage>
      <Box css={datePickerWrapperStyle}>
        <SessionsToDisplay
          isEditing={isEditing}
          removeAppEventSession={removeAppEventSession}
          sessions={sessions}
          showInfoAlert={showInfoAlert}
          updateAppEventSession={updateAppEventSession}
        />

        {isEditing && (
          <Button title="Add session" variant="outline" size="sm" onClick={handleClick} />
        )}
      </Box>
    </>
  )
}

Sessions.propTypes = {
  isEditing: bool,
  onAppEventChange: func,
  sessions: array,
  showInfoAlert: bool
}

export default Sessions
